// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * Item 8 step 7 (2026-09-12, moved here 2026-09-13 per independent review —
 * one authoritative owner instead of two byte-identical copies in
 * frontend/src/lib/entitlement.ts and mobile/src/lib/entitlement.ts, the
 * exact duplication class AGENTS.md's "one owner per business rule" rule
 * warns about): does this account currently hold ANY grant that lets it
 * into the app? Unlike each client's own `isActive` (which deliberately
 * excludes `pilot` so the free-claim hero card still renders for a
 * grandfathered pilot holder), this treats every source identically —
 * `me.ts` (`readBestEntitlement`) already re-verifies expiry for one-time
 * plans (free-claim/prelaunch) and folds every source into the same
 * handful of wire states before this ever reaches the client, so no
 * source-specific branching belongs here.
 *
 * `past_due` is included for parity with `privacy.ts`'s own definition of
 * a "live" subscription (`state IN ('trialing','active','past_due')`) —
 * `me.ts` doesn't emit it today (a past_due `entitlements` row is filtered
 * out before it ever reaches `readBestEntitlement`'s candidate list), so
 * this has no effect on current behavior; it's here so this predicate
 * doesn't quietly start blocking a state the schema already reserves for
 * "still owed access, being retried" if that gap is ever closed.
 */
export function hasAppAccess(me) {
    return (me.entitlement.state === "active" ||
        me.entitlement.state === "trialing" ||
        me.entitlement.state === "past_due");
}
/**
 * Item 8 (2026-09-12, moved here 2026-09-13 per ultrareview — the same
 * "two byte-identical copies" duplication this file was already created
 * to fix for `hasAppAccess`, found in `AccessPage.tsx` and `access.tsx`):
 * both the retired pre-launch free month and its replacement, the 2-week
 * free claim, get identical "free tier" treatment across each client's
 * Access screen (price display, upgrade-path copy, cancel-hint
 * suppression) — one shared predicate instead of a
 * `planKey === "prelaunch" || planKey === "freeClaim"` check repeated
 * inline at every call site.
 */
export function isFreeTierPlan(planKey) {
    return planKey === "prelaunch" || planKey === "freeClaim";
}
/**
 * ADR-085 IAP-4: the account's OWN provider-granted entitlement (an `individual` or `b2b` row that is active or trialing) —
 * deliberately NOT the automatic no-charge `pilot` grant that `hasAppAccess` also counts. One owner for the web Access page, the
 * mobile Apple paywall and `applePastDueState` below (the mobile copy of this predicate was removed in the same change).
 */
export function hasIndividualAccess(me) {
    return ((me.entitlement.source === "individual" || me.entitlement.source === "b2b") &&
        (me.entitlement.state === "active" || me.entitlement.state === "trialing"));
}
/**
 * ADR-085 IAP-4: a past-due Apple row. Inside Apple's billing-grace period access continues ("grace": the account still holds its
 * provider entitlement and the period end is in the future); after it, access is paused ("after_grace"), unless a pilot grant still
 * lets the account in — then "paused" would be untrue and no claim is made ("none"). Any other row (not Apple, not past due) is "none".
 * One owner for both clients (it was `pastDueState` in the mobile purchase flow; behaviour identical).
 */
export function applePastDueState(sub, me, now) {
    if (!sub || sub.provider !== "apple" || sub.state !== "past_due")
        return "none";
    const end = sub.currentPeriodEnd ? Date.parse(sub.currentPeriodEnd) : Number.NaN;
    const inGrace = hasIndividualAccess(me) && Number.isFinite(end) && end > now;
    if (inGrace)
        return "grace";
    return hasAppAccess(me) ? "none" : "after_grace";
}
/**
 * ADR-085 D7 / audit A-13: deleting a BiteBalance account does NOT cancel an Apple subscription — Apple keeps charging. True for an
 * Apple row that is active or past due and not already set to end; the deletion screens of BOTH clients warn exactly then (the mobile
 * source-contract test pins that it uses this helper).
 */
export function appleSubscriptionOutlivesDeletion(sub) {
    return !!sub && sub.provider === "apple" && (sub.state === "active" || sub.state === "past_due") && !sub.cancelAtPeriodEnd;
}
//# sourceMappingURL=entitlement.js.map